import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type TransitionEvent as ReactTransitionEvent,
} from "react";

import { useSubmitShortcut } from "@/lib/useSubmitShortcut";
import { cx } from "@/lib/cx";
import { playSfx } from "@/platform/sfx";
import { errorMessage } from "@/lib/errors";
import { Alert } from "@/ui/Alert";
import { Button } from "@/ui/Button";
import { KeyHint } from "@/ui/KeyHint";
import { Dialog, DialogFooter } from "./Dialog";
import { ChevronLeftIcon, CloseIcon } from "@/ui/icons";
import { ScrollShadow } from "./ScrollShadow";
import { LAYER, sheetSurface } from "./surface";
import { OverlayHeader } from "./OverlayHeader";
import { useBackClose } from "./useBackClose";
import { useIsCompact } from "./useMediaQuery";

/**
 * The peek — the structural change the rewrite is built around.
 *
 * UI-REVIEW §6 and P3/P4/P11 are one problem wearing three faces: editing
 * anything means either exploding a 40px row into a 600px inline form or
 * opening a modal that blocks the list you were working through. Both make the
 * common case — look at a card, decide, look at the next one — cost a full
 * open/close cycle each time.
 *
 * So this panel is deliberately **not modal**:
 *
 * - it does not trap focus and does not mark the page inert, so the list behind
 *   it stays reachable from the keyboard;
 * - opening another row calls `open()` again with a different `key` and the
 *   contents **swap in place** (with the pointer that is now two clicks: one
 *   to close, one on the row);
 * - it owns **one dirty state and one Save** for whatever is inside it
 *   ({@link usePeekForm}), and closing or swapping while dirty asks first.
 *   That is the direct fix for P3, where typing a description and clicking
 *   "Done" silently threw it away.
 *
 * ## On a desktop it is a centred panel, not a side rail
 *
 * It began as a 420px rail welded to the right edge, and the owner asked for the
 * opposite: one large panel in the middle of the screen, where the eye already
 * is. So on a desktop it is now centred, up to {@link PEEK_WIDTH} wide and 86dvh
 * tall, with the full sheet radius on all four corners.
 *
 * The three facts above survive that move, because none of them were about
 * *where* the panel sat:
 *
 * - a click away only closes the panel: the scrim takes the pointer on a
 *   desktop too, so the click never reaches the row under it (it used to pass
 *   through, which swapped rows in one click but also ticked checkboxes by
 *   accident — Pipe chose "close only" on 2026-10-16);
 * - the panel no longer insets `<main>`, because a centred panel cannot sit
 *   *beside* a board. `--busee-peek-width` is gone with the rail that needed it.
 *
 * On a phone it is still a bottom sheet, because a centred panel on a 375px
 * screen is just a covered screen with wasted margins. There the same scrim is a
 * real modal backdrop and the sheet gains drag-to-dismiss.
 *
 * ## Using it
 *
 * ```tsx
 * const peek = usePeek();
 * peek.open({ key: `card:${card.id}`, title: card.title, titleInBody: true, content: <CardPeek card={card} /> });
 * ```
 *
 * A caller explicitly says whether its title editor lives in the body. When it
 * does, the header carries the record identity instead of repeating the title.
 * Inside the content, to get the standard Save/Cancel footer and dirty guard:
 *
 * ```tsx
 * usePeekForm({ dirty, onSave: () => save(draft), onDiscard: () => reset() });
 * ```
 */

/**
 * The centred panel's maximum width on a desktop, in pixels.
 *
 * It is a cap, not a width: the panel is `100vw` minus a margin below this, so
 * a narrow window still gets a panel with air around it rather than one that
 * runs off both edges.
 */
export const PEEK_WIDTH = 780;

export type PeekEntry = {
  /**
   * Identity of the thing being peeked, e.g. `card:abc123`.
   *
   * Opening a different key swaps the contents; opening the same key again is
   * a no-op refresh rather than a close-and-reopen, so re-clicking the open row
   * does not throw away scroll position or an in-progress edit.
   */
  key: string;
  title: ReactNode;
  /**
   * True when the body already owns the title field. The header then promotes
   * `subtitle` into its h2 rather than presenting a duplicate title.
   */
  titleInBody: boolean;
  /** A line under the title — the issue key, the project, the date. */
  subtitle?: ReactNode;
  /** Header controls, trailing. Usually one {@link OverflowMenu}. */
  headerActions?: ReactNode;
  content: ReactNode;
  /**
   * Footer for a peek with no form. Ignored once a {@link usePeekForm} inside
   * the content registers, because then the peek owns the footer.
   */
  footer?: ReactNode;
  /** Overrides {@link PEEK_WIDTH} — the desktop panel's maximum width. */
  width?: number;
};

export type PeekApi = {
  open: (entry: PeekEntry) => void;
  close: () => void;
  /** Patch the open entry — a renamed title, a new subtitle. */
  update: (patch: Partial<Omit<PeekEntry, "key">> & { key: string }) => void;
  isOpen: boolean;
  /** The key currently on screen, so a list can mark that row as selected. */
  openKey: string | null;
};

export type PeekFormConfig = {
  /** Whether there is anything unsaved. Drives Save and the close guard. */
  dirty: boolean;
  /** May be async; the footer shows a spinner and blocks a second click. */
  onSave: () => void | Promise<void>;
  /** Called when the user confirms a discard. Reset your draft here. */
  onDiscard?: () => void;
  /** Defaults to "Save". */
  saveLabel?: string;
  /** The form also saves on ⌘↵ / Ctrl ↵: the Save button shows the chord. */
  saveShortcut?: boolean;
  /** Blocks Save even when dirty — an invalid draft. */
  canSave?: boolean;
  /**
   * Close the peek after a **successful** save. Defaults to `true`.
   *
   * Save is a terminal action: you came here to change one record, you changed
   * it, and the panel is now a lid over the board you want to look at. Leaving
   * it open shipped a peek that had nothing left to say and no longer had a
   * dirty state, so the only remaining affordance was the Cancel button — and
   * a screen whose next step is "press Cancel" after a success is a screen that
   * asks the user to undo their own achievement.
   *
   * It defaults to true rather than being set per call site because the two
   * existing call sites both want it, and the next one almost certainly does
   * too. A peek that genuinely must stay open (a log you keep adding rows to)
   * opts out here, in one obvious place.
   *
   * **Only a success closes.** A throw from `onSave` leaves the peek open with
   * the message above the footer and the user's text intact — see `save()`.
   * ⌘↵ is bound to the same `save()`, so it inherits this without a second
   * code path.
   */
  closeOnSave?: boolean;
  /**
   * Offer both a "Save" that keeps the peek open and a primary "Save & close". For an
   * editor you keep working in (a task's description) after each save.
   */
  saveAndStay?: boolean;
  /**
   * Save is enabled while the form is clean. For a form whose Save is an action on what
   * is already filled in (send a ready brief to the agents), not a write of an edit.
   */
  saveWhenClean?: boolean;
  /** A visible workflow action on the leading side of the form footer. */
  secondaryAction?: {
    label: ReactNode;
    onSelect: () => void;
    disabled?: boolean;
    icon?: ReactNode;
  };
};

export type PeekFormControl = {
  saving: boolean;
  /** Same as pressing Save. Bind it to ⌘↵ if the body wants that. */
  save: () => void;
  /** Same as pressing Cancel — asks first when dirty. */
  requestClose: () => void;
};

/* -------------------------------------------------------------------------- */

type Registration = {
  dirty: boolean;
  canSave: boolean;
  saveAndStay: boolean;
  saveWhenClean: boolean;
  saveLabel: string;
  saveShortcut: boolean;
  saving: boolean;
  /** `close: false` is the stay-open Save of `saveAndStay`. */
  save: (options?: { close?: boolean }) => void;
  discard: () => void;
  secondaryAction?: PeekFormConfig["secondaryAction"];
};

type PeekInternals = {
  registerForm: (registration: Registration) => () => void;
  /** The Cancel path: asks about unsaved work first. */
  requestClose: () => void;
  /**
   * Close without asking. The **only** legitimate caller is a save that has
   * already resolved: at that moment the work is committed, so the dirty guard
   * would be asking the user to confirm discarding something that is already on
   * the server. Everything else goes through {@link requestClose}.
   */
  closeNow: () => void;
};

const PeekContext = createContext<PeekApi | null>(null);
const PeekInternalContext = createContext<PeekInternals | null>(null);

export function usePeek(): PeekApi {
  const api = useContext(PeekContext);
  if (!api) {
    throw new Error("usePeek() requires <OverlayProvider> (or <PeekProvider>) above it.");
  }
  return api;
}

/** Just the open/width facts, for a shell that wants to inset its content. */
export function usePeekState(): { open: boolean; key: string | null } {
  const { isOpen, openKey } = usePeek();
  return { open: isOpen, key: openKey };
}

/**
 * Declare the peek's form from inside its content.
 *
 * The peek renders the footer, so there is exactly one Save button and exactly
 * one dirty flag no matter how many fields the body has — which is the whole
 * point. The old task editor had four commit affordances with two different
 * truths about whether your text survived.
 */
export function usePeekForm(config: PeekFormConfig): PeekFormControl {
  const internals = useContext(PeekInternalContext);
  if (!internals) {
    throw new Error("usePeekForm() may only be called inside a peek's content.");
  }

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handlers change identity every render; the registration must not, or the
  // footer would re-register on every keystroke in the body.
  const latest = useRef(config);
  latest.current = config;
  const savingRef = useRef(false);
  const { requestClose, closeNow } = internals;

  const save = useCallback((options?: { close?: boolean }) => {
    if (savingRef.current) {
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError(null);
    void (async () => {
      try {
        await latest.current.onSave();
        // Defaults to closing — see `closeOnSave`. `?? true` rather than a
        // destructured default, because this reads the *latest* config out of
        // a ref rather than off a props object.
        if (options?.close ?? latest.current.closeOnSave ?? true) {
          // `close()`, not `requestClose()`. The latter is the Cancel path and
          // asks "discard your changes?" when the form is dirty — and the form
          // is still dirty here, because the caller's own state has not caught
          // up with the write that just succeeded. Asking someone to confirm
          // discarding the edit they just saved is the exact failure this
          // whole change exists to remove.
          closeNow();
        }
      } catch (failure) {
        // A failed save must not close and must not look like it worked. The
        // message is rendered above the footer, in the panel that still holds
        // the user's text.
        setError(errorMessage(failure));
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    })();
  }, [closeNow]);

  const discard = useCallback(() => {
    setError(null);
    latest.current.onDiscard?.();
  }, []);

  const { registerForm } = internals;
  const { dirty, canSave = true, saveLabel = "Save", saveShortcut = false, saveAndStay = false, saveWhenClean = false, secondaryAction } = config;

  useEffect(
    () => registerForm({ dirty, canSave, saveAndStay, saveWhenClean, saveLabel, saveShortcut, saving, save, discard, secondaryAction }),
    [registerForm, dirty, canSave, saveAndStay, saveWhenClean, saveLabel, saveShortcut, saving, save, discard, secondaryAction],
  );

  // A failed save is published up to the panel, which renders it immediately
  // above the footer. Putting it there rather than in the body means it cannot
  // be scrolled out of sight in a long form — the one place a "did that work?"
  // message must never hide.
  usePeekError(error);

  // The body's save takes no options: callers hand it to fields as an `onCommit(next)`.
  const saveAndClose = useCallback(() => save(), [save]);
  return { saving, save: saveAndClose, requestClose };
}

/* Error plumbing: `usePeekForm` sets it, the panel renders it directly above
   the footer so it cannot be scrolled out of sight. */
const PeekErrorContext = createContext<((message: string | null) => void) | null>(null);

function usePeekError(message: string | null) {
  const publish = useContext(PeekErrorContext);
  useEffect(() => {
    publish?.(message);
    return () => publish?.(null);
  }, [publish, message]);
}

/* -------------------------------------------------------------------------- */

export function PeekProvider({ children }: { children: ReactNode }) {
  const [entry, setEntry] = useState<PeekEntry | null>(null);
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [error, setError] = useState<string | null>(null);
  // What close/swap is waiting on the discard question. `null` = nothing asked.
  const [pending, setPending] = useState<{ next: PeekEntry | null } | null>(null);

  // Both are read synchronously from a click handler, after the render that
  // produced them, so a ref is the correct shape here — reading `entry` out of
  // the closure would answer with whatever was true when `attempt` was created.
  const entryRef = useRef<PeekEntry | null>(null);
  const registrationRef = useRef<Registration | null>(null);

  useEffect(() => {
    entryRef.current = entry;
  }, [entry]);

  // The memory-card chime when the panel appears; swapping rows keeps it quiet.
  const isOpen = entry !== null;
  useEffect(() => {
    if (isOpen) {
      playSfx("open");
    }
  }, [isOpen]);

  /** Called once the exit animation has finished; see {@link PeekPanel}. */
  const clear = useCallback(() => {
    setRegistration(null);
    registrationRef.current = null;
    setError(null);
  }, []);

  const registerForm = useCallback((next: Registration) => {
    setRegistration(next);
    registrationRef.current = next;
    return () => {
      registrationRef.current = null;
      setRegistration((current) => (current === next ? null : current));
    };
  }, []);

  /**
   * Go to `next`, or close when it is null, asking first if there is unsaved
   * work. Re-opening the key that is already open is a refresh, not a swap —
   * clicking the row you are already editing must not ask about your own edits.
   */
  const attempt = useCallback((next: PeekEntry | null) => {
    const current = entryRef.current;
    const sameThing = next !== null && current !== null && current.key === next.key;

    if (sameThing) {
      entryRef.current = next;
      setEntry(next);
      return;
    }
    // A save is already an explicit decision to commit the draft.  Ignore a
    // close/swap signal until it settles: the pointer/keyboard event that
    // started the save (or an outside click that lands while the request is in
    // flight) must never briefly raise the destructive-looking discard dialog
    // before `closeNow()` closes the saved peek.
    if (registrationRef.current?.saving) {
      return;
    }
    if (registrationRef.current?.dirty) {
      setPending({ next });
      return;
    }

    entryRef.current = next;
    setEntry(next);
    if (next) {
      // A swap replaces the content now, so its form registration goes with it.
      // A close leaves both alone: the footer has to survive the exit
      // animation, and `clear` runs when that finishes.
      setRegistration(null);
      registrationRef.current = null;
      setError(null);
    }
  }, []);

  // On a phone the peek is a full-screen page, and Back is how you leave a
  // page. It takes the Cancel path, so unsaved edits still ask first.
  useBackClose(isOpen, () => attempt(null));

  const api = useMemo<PeekApi>(
    () => ({
      open: attempt,
      close: () => attempt(null),
      update: (patch) =>
        setEntry((current) =>
          current && current.key === patch.key ? { ...current, ...patch } : current,
        ),
      isOpen: entry !== null,
      openKey: entry?.key ?? null,
    }),
    [attempt, entry],
  );

  const internals = useMemo<PeekInternals>(
    () => ({
      registerForm,
      requestClose: () => attempt(null),
      // Straight to the state change, past `attempt`'s dirty guard. The
      // registration is dropped first so nothing can re-assert `dirty` between
      // here and the exit animation finishing.
      closeNow: () => {
        registrationRef.current = null;
        setRegistration(null);
        setPending(null);
        setError(null);
        entryRef.current = null;
        setEntry(null);
      },
    }),
    [attempt, registerForm],
  );

  const resolvePending = useCallback(
    (discard: boolean) => {
      const request = pending;
      setPending(null);
      if (!request) {
        return;
      }
      if (!discard) {
        return;
      }
      registrationRef.current?.discard();
      registrationRef.current = null;
      setRegistration(null);
      setError(null);
      entryRef.current = request.next;
      setEntry(request.next);
    },
    [pending],
  );

  return (
    <PeekContext.Provider value={api}>
      <PeekInternalContext.Provider value={internals}>
        <PeekErrorContext.Provider value={setError}>
          {children}

          <PeekPanel
            entry={entry}
            registration={registration}
            error={error}
            onRequestClose={() => attempt(null)}
            onClosed={clear}
          />

          <Dialog
            open={pending !== null}
            onOpenChange={(next) => {
              if (!next) {
                resolvePending(false);
              }
            }}
            size="sm"
            title="Discard your changes?"
            description="This panel has edits that have not been saved. Closing it now throws them away."
            footer={
              <DialogFooter
                cancel={
                  <Button variant="secondary" onClick={() => resolvePending(false)}>
                    Keep editing
                  </Button>
                }
                primary={
                  <Button variant="danger" onClick={() => resolvePending(true)}>
                    Discard
                  </Button>
                }
              />
            }
          >
            <p className="text-body text-muted">
              Nothing has been sent to the server yet — closing is the only thing that loses them.
            </p>
          </Dialog>
        </PeekErrorContext.Provider>
      </PeekInternalContext.Provider>
    </PeekContext.Provider>
  );
}

/* -------------------------------------------------------------------------- */

/*
 * There were two detents here, and a grabber you dragged between them.
 *
 * Both are gone, at the owner's request (2026-08-21): "เวลาดึง HandleBar เพื่อ
 * Resize ขึ้นลงแล้วมันกระตุกๆ". He was right, and the reason is structural
 * rather than a tuning problem. The gesture resized the panel by writing
 * `height` in pixels on every `pointermove`, and a height change is a layout
 * change — so all 115 elements inside the panel were measured again on every
 * frame of the drag. Measured on a Mac: **4% of frames missed the 16.7ms
 * budget, worst 24ms**. With the panel's contents taken out of layout the same
 * gesture missed **0%**, worst 12.8ms, which is what pins the cost on the
 * relayout rather than on anything else. A phone has less CPU and three times
 * the pixels, so what reads as "a bit sticky" on the Mac is what he was feeling
 * on the handset.
 *
 * The fix is not a faster drag. On a phone, a record's detail is a *place you
 * go*, not a drawer you hold open at a height of your choosing — every phone
 * app that shows a list and a detail does it that way, and there is nothing to
 * see behind the panel at 92% anyway. So below the sidebar breakpoint the peek
 * is now a full-screen page with a back button, and there is no gesture left to
 * stutter.
 */

function PeekPanel({
  entry,
  registration,
  error,
  onRequestClose,
  onClosed,
}: {
  entry: PeekEntry | null;
  registration: Registration | null;
  error: string | null;
  onRequestClose: () => void;
  onClosed: () => void;
}) {
  const compact = useIsCompact();
  const panelRef = useRef<HTMLDivElement>(null);
  // A form that offers ⌘↵ saves from anywhere in the panel, focused field or not.
  useSubmitShortcut(
    panelRef,
    () => {
      if ((registration?.dirty || registration?.saveWhenClean) && registration.canSave && !registration.saving) registration.save();
    },
    registration?.saveShortcut === true,
  );
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  // `entry` is the request; `shown` is what is on screen. They differ for one
  // frame on open (so the enter transition has a start state) and for the
  // length of the exit transition on close.
  const [shown, setShown] = useState<PeekEntry | null>(entry);
  const [visible, setVisible] = useState(false);

  useLayoutEffect(() => {
    if (entry) {
      setShown(entry);
      return;
    }
    setVisible(false);
  }, [entry]);

  useEffect(() => {
    if (!entry) {
      return;
    }
    // One frame with `data-state="closed"` before flipping, or the browser
    // never sees a transition — it just paints the final position.
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, [entry]);

  // Remember where focus was, and put it back — but only if the peek still
  // holds it. A user who clicked into the list behind the panel should not have
  // the caret yanked away when the panel closes.
  useEffect(() => {
    if (entry && !restoreFocusTo.current) {
      restoreFocusTo.current = document.activeElement as HTMLElement | null;
      panelRef.current?.focus({ preventScroll: true });
    }
  }, [entry]);

  // A sheet is modal even though the side panel is not.
  //
  // The non-modal contract — no focus trap, no inert, no scrim — is the whole
  // point of the desktop panel: the list behind it stays visible and clickable,
  // which is what makes triaging 67 cards possible. None of that is true of the
  // compact variant. It draws a scrim, it covers up to 85dvh of the viewport,
  // and the list behind it is *gone*. Leaving it non-modal there meant Tab and
  // a screen-reader swipe walked straight into a list nobody can see, while a
  // pointer could not reach the same rows at all.
  //
  // `inert` on the siblings rather than a hand-rolled focus trap: it removes
  // them from the tab order *and* from the accessibility tree in one move, and
  // it cannot get out of step with a DOM that is changing underneath it. The
  // panel's siblings are the app shell (everything the sheet covers) and the
  // toast viewport, which is deliberately left live — it sits above the sheet
  // at `LAYER.toast`, and Undo is this app's answer to a destructive action
  // including one taken from inside the peek.
  useEffect(() => {
    const panel = panelRef.current;
    const parent = panel?.parentElement;
    if (!compact || !visible || !panel || !parent) {
      return;
    }

    const inerted: HTMLElement[] = [];
    for (const node of Array.from(parent.children)) {
      if (!(node instanceof HTMLElement) || node === panel || node.inert) {
        continue;
      }
      if (node.dataset.buseePeekScrim !== undefined || node.dataset.buseeToastViewport !== undefined) {
        continue;
      }
      node.inert = true;
      inerted.push(node);
    }

    return () => {
      for (const node of inerted) {
        node.inert = false;
      }
    };
  }, [compact, visible]);

  const finishClose = useCallback(() => {
    setShown(null);
    const target = restoreFocusTo.current;
    restoreFocusTo.current = null;
    if (target && document.body.contains(target)) {
      target.focus({ preventScroll: true });
    }
    onClosed();
  }, [onClosed]);

  const onTransitionEnd = (event: ReactTransitionEvent<HTMLDivElement>) => {
    if (!visible && !entry && event.propertyName === "opacity" && event.target === panelRef.current) {
      finishClose();
    }
  };

  // A backgrounded tab does not fire `transitionend`, and neither does a
  // browser that decided the element was not visible enough to animate — either
  // way the panel would be left mounted and invisible, still swallowing clicks.
  useEffect(() => {
    if (entry || !shown || visible) {
      return;
    }
    const id = window.setTimeout(finishClose, 500);
    return () => window.clearTimeout(id);
  }, [entry, shown, visible, finishClose]);

  // A cap, not a width — the panel is `100vw` minus a margin below this. The
  // rail this replaced also published the number as `--busee-peek-width` so the
  // shell could inset `<main>` and let a board sit beside it; a centred panel
  // has nothing to sit beside, so that variable and both of its readers are
  // gone rather than left publishing a number nobody acts on.
  const width = shown?.width ?? PEEK_WIDTH;

  if (!shown) {
    return null;
  }

  const state = visible ? "open" : "closed";

  return (
    <>
      {/* The scrim takes the pointer on every screen size, so a click away
          only closes the panel. It used to be `pointer-events-none` on a
          desktop, with a document listener doing the closing and letting the
          click through so another row could swap the panel in one click — but
          that same click also landed on whatever was under it, and Pipe kept
          ticking a task's checkbox by accident while dismissing the panel. A
          closing scrim stops taking the pointer (overlay.css), so the next
          click after it fades reaches the page. */}
      <div
        data-busee-peek-scrim
        data-state={state}
        onClick={onRequestClose}
        className={cx("fixed inset-0 bg-black/30", LAYER.peek)}
      />

      <div
        ref={panelRef}
        data-busee-peek
        data-state={state}
        data-variant={compact ? "page" : "center"}
        // The compact sheet genuinely is modal — it covers the list and marks
        // its siblings `inert` — so it says so. The desktop panel is
        // deliberately `complementary`: giving it `aria-modal` would announce a
        // trap that does not exist and is the one property the redesign is
        // built on.
        role={compact ? "dialog" : "complementary"}
        aria-modal={compact ? true : undefined}
        aria-label={typeof shown.title === "string" ? shown.title : "Details"}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            onRequestClose();
          }
        }}
        onTransitionEnd={onTransitionEnd}
        // The compact page declares no height at all now: `inset-0` gives it
        // the viewport, and nothing about it changes while it is open. That is
        // the whole performance story — there is no per-frame geometry left.
        style={compact ? undefined : { maxWidth: `${width}px` }}
        className={cx(
          sheetSurface,
          "shadow-sheet",
          LAYER.peek,
          "fixed [--busee-chrome-inset-end:0px] [--busee-page-inset:var(--spacing-pad-tight)]",
          compact
            ? // A full-screen page, not a drawer. It covers the tab bar on
              // purpose: while you are inside one task, switching section is
              // not the move — Back is, and Back is the first thing in the bar.
              // `pt-[env(safe-area-inset-top)]` keeps the toolbar out from
              // under the status bar, which a bottom sheet never had to think
              // about because it never reached the top of the screen.
              "inset-0 rounded-none pt-[env(safe-area-inset-top,0px)]"
            : // Centred exactly the way `Dialog` centres — `top/left: 50%` with
              // a `-50%` translate, no declared height, capped by `max-height`
              // — because that shape is already proven inside this app's macOS
              // WKWebView, and the obvious-looking alternative is not.
              //
              // It was `inset-0 m-auto h-fit max-h-[86dvh]` for one build, and
              // that shipped a panel ~20px tall: with both insets at 0 and
              // `height: fit-content`, WebKit resolved the fit-content height
              // from the flex column's *min-content* — and the scroll area
              // carries `min-height: 0`, so its min-content is 0. The panel
              // came out as header + nothing + footer. Chromium (the dev
              // preview) and Playwright's WebKit both resolved the same CSS
              // against the available height instead and looked perfect, which
              // is why only the packaged app showed it.
              //
              // With no `height` at all the panel is shrink-to-fit, so it still
              // hugs a short peek instead of being a mostly-empty slab, and the
              // centring transform is written in `overlay.css` next to the exit
              // transform so the two cannot disagree about `transform`.
              "top-1/2 left-1/2 w-[calc(100vw-2rem)] max-h-[calc(100dvh-2rem)]",
        )}
      >
        <OverlayHeader
          titleLabel={
            shown.titleInBody && typeof shown.subtitle === "string"
              ? shown.subtitle
              : typeof shown.title === "string"
                ? shown.title
                : "Details"
          }
          title={shown.titleInBody ? shown.subtitle : shown.title}
          meta={shown.titleInBody ? undefined : shown.subtitle}
          // A back chevron on a phone, a ✕ on a desktop, and the difference is
          // not decoration: a full-screen page is somewhere you *went*, and the
          // control that leaves it belongs at the leading edge where a thumb
          // and every other phone app expect it. A panel floating over a
          // visible list is something you *opened*, and that closes from the
          // trailing corner. Same handler, same accessible name, so nothing
          // that looks for "Close details" has to know which one it got.
          actions={shown.headerActions}
          onClose={onRequestClose}
          closeLabel="Close details"
          closeIcon={compact ? <ChevronLeftIcon /> : <CloseIcon />}
          className={compact ? "[&>button:last-child]:-order-1" : undefined}
        />

        {/* The page fills the window; its *content* does not. At 1024px a
            full-bleed body stretched every field to a thousand pixels, which is
            a form nobody can scan. The bars stay edge to edge — a bar that
            stopped short of the corner would look broken — and only the column
            you read is capped, on the same measure the app's `measure` routes
            use. Below that width the cap never binds and this is a no-op. */}
        <ScrollShadow contentClassName={cx("p-3", compact && "mx-auto w-full max-w-[47.5rem]")}>
          {shown.content}
        </ScrollShadow>

        {error ? (
          <div className="shrink-0 px-3 pt-2">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {registration ? (
          <div
            className="flex min-h-10 shrink-0 items-center gap-2 hairline-t px-3"
            style={{ paddingBottom: compact ? "env(safe-area-inset-bottom, 0px)" : undefined }}
          >
            {/* One dirty state, one Save. Save is disabled when there is
                nothing to save, so "is my typing safe?" is answerable from the
                button alone — which is exactly what P3 could not answer. */}
            {registration.secondaryAction ? (
              <Button
                variant="tinted"
                size="sm"
                icon={registration.secondaryAction.icon}
                onClick={registration.secondaryAction.onSelect}
                disabled={registration.secondaryAction.disabled || registration.saving}
              >
                {registration.secondaryAction.label}
              </Button>
            ) : null}
            <span className="min-w-0 flex-1 text-caption text-muted">
              {registration.dirty && !compact ? "Unsaved changes" : null}
            </span>
            {/* On a phone the page has its own back button, and the footer needs the room for two saves. */}
            {compact && registration.saveAndStay ? null : (
              <Button variant="ghost" size="sm" onClick={onRequestClose} disabled={registration.saving}>
                Cancel
              </Button>
            )}
            {registration.saveAndStay ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => registration.save({ close: false })}
                disabled={registration.saving || !registration.dirty || !registration.canSave}
                title="Save and keep this open"
              >
                {registration.saveLabel}
              </Button>
            ) : null}
            <Button
              variant="primary"
              size="sm"
              onClick={() => registration.save()}
              loading={registration.saving}
              disabled={!(registration.dirty || registration.saveWhenClean) || !registration.canSave}
              title={registration.saveAndStay ? "Save and close" : undefined}
            >
              {registration.saveAndStay ? `${registration.saveLabel} & close` : registration.saveLabel}
              {registration.saveShortcut ? <KeyHint /> : null}
            </Button>
          </div>
        ) : shown.footer ? (
          <div className="flex min-h-10 shrink-0 items-center hairline-t px-3">{shown.footer}</div>
        ) : null}
      </div>
    </>
  );
}
